import { Tooltip } from "@mantine/core"
import { resolveSpeakerColor } from "../../lib/speaker-colors"
import type { IProps } from "./interfaces"

import classes from "./styles.module.css"

/** One resolved bar segment — the shape both the VM and the positional path
 * collapse to before rendering. */
interface ISegment {
  name: string
  seconds: number
  color: string
}

/**
 * TalkShare — slim stacked bar visualising each speaker's share of talking
 * time. Pure presentational; renders `null` when there is no recorded time.
 *
 * Two inputs, one render: pass unified `speakers` (VM `displayName` +
 * `resolvedColor` + `talkSeconds`) or the legacy positional `share` +
 * `speakerNames` + `colorMap`. Positional colours resolve through the single
 * canonical chain (`resolveSpeakerColor`, audit 3.13) — the per-meeting map when
 * supplied, else the shared deterministic hash — so the same person keeps one
 * hue across the legend, the summary cards, and this bar.
 */
export const TalkShare = ({ share, speakerNames, colorMap, speakers }: IProps): React.JSX.Element | null => {
  const segments: ISegment[] = speakers
    ? speakers.map((vm) => ({ name: vm.displayName, seconds: vm.talkSeconds, color: vm.resolvedColor }))
    : (share ?? []).map((seconds, i) => {
        const name = speakerNames?.[i] ?? `Speaker ${i + 1}`
        return { name, seconds, color: resolveSpeakerColor(name, colorMap) }
      })

  const total = segments.reduce((acc, s) => acc + s.seconds, 0)
  if (total <= 0) return null

  return (
    <div className={classes.share}>
      {segments.map((seg) => {
        const pct = (seg.seconds / total) * 100
        if (pct <= 0) return null
        const minutes = Math.max(1, Math.round(seg.seconds / 60))
        const label = `${seg.name}: ${minutes}m (${pct.toFixed(0)}%)`
        return (
          <Tooltip key={seg.name} label={label} withArrow position="top" openDelay={150}>
            <span style={{ width: `${pct}%`, background: seg.color, cursor: "help" }} />
          </Tooltip>
        )
      })}
    </div>
  )
}
